import React, { useContext, useState } from 'react'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import API from '../../../../services/API'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HistoryIcon from '@mui/icons-material/History'
import moment from 'moment'
import { SiteModeContext } from '../../../../components/mainFrame/MainFrame'
import { useNavigate } from 'react-router-dom'
import useIsMobile from '../../../../helpers/useIsMobile'

const AllControlRooms = () => {
  const isMobile = useIsMobile()
  const [expanded, setExpanded] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState(false)
  const [rooms, setRooms] = useState([])

  const { setSiteMode, setSelectedControlRoom } = useContext(SiteModeContext)
  const navigate = useNavigate()

  const loadRooms = () => {
    if (hasLoaded || loading) return

    setLoading(true)
    setFetchError(false)

    API.get('/commandcontrol/controlroomlist?skip=0')
      .then((res) => {
        if (res.status === 200) {
          setRooms(res.data)
          setHasLoaded(true)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded)

    if (isExpanded) {
      loadRooms()
    }
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={handleAccordionChange}
      disableGutters
      elevation={0}
      sx={{
        mt: 2,
        borderRadius: '16px !important',
        border: '1px solid #E2E8F0',
        overflow: 'hidden',
        boxShadow: '0 10px 28px rgba(36,47,64,0.06)',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 2.5,
          py: 1,
          backgroundColor: '#F8FAFC',
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #242F40 0%, #239AA4 130%)',
              color: 'white',
            }}
          >
            <HistoryIcon />
          </Box>

          <Stack>
            <Typography fontWeight={900} color="#242F40">
              All Control Rooms
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View historical and inactive control rooms.
            </Typography>
          </Stack>
        </Stack>

        {hasLoaded && (
          <Chip
            label={`${rooms.length} loaded`}
            size="small"
            sx={{
              borderRadius: 999,
              backgroundColor: '#E6F7F9',
              color: '#1E7F88',
              fontWeight: 800,
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          />
        )}
      </AccordionSummary>

      <AccordionDetails sx={{ p: { xs: 1.5, md: 2.5 } }}>
        <CustomDataGrid
          rows={rooms}
          loading={loading}
          // Phones: keep the identifying columns + status; the long
          // "started by" column stays reachable via the column picker.
          columnVisibilityModel={isMobile ? { started: false } : undefined}
          columns={[
            { field: 'controlRoomRef', headerName: 'ID', minWidth: 60, width: 70 },
            {
              field: 'title',
              valueGetter: (value, row) =>
                row?.customTitle ?? row?.linkedEvent?.title ?? 'Unknown',
              headerName: 'Control Room',
              flex: 1,
              minWidth: 160,
            },
            {
              field: 'started',
              headerName: 'Started',
              renderCell: ({ value, row }) =>
                `${moment(value).format('DD/MM/YYYY HH:mm')} by ${
                  row.owner?.firstName ?? ''
                } ${row.owner?.lastName ?? ''}`,
              minWidth: 350,
              flex: 1,
            },
            {
              field: 'status',
              headerName: 'Status',
              minWidth: 120,
              flex: 0.6,
            },
          ]}
          getRowId={(row) => row._id}
          slots={{
            noRowsOverlay: () =>
              fetchError ? (
                <Alert severity="error">
                  Unable to load Control Rooms. Please try again later.
                </Alert>
              ) : (
                <Alert severity="info">Nothing to see here yet.</Alert>
              ),
            toolbar: () => null,
            footer: () => null,
          }}
          disableRowSelectionOnClick
          onRowClick={({ row }) => {
            navigate(`/commandcontrol/controlroom/${row._id}`)
          }}
        />
      </AccordionDetails>
    </Accordion>
  )
}

export default AllControlRooms
